/* eslint-disable react-hooks/exhaustive-deps */
import { PAGE_SEARCH_PARAMETER } from "constants/common";
import { t } from "i18next";
import moment from "moment";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button, DateRangeInput, RadioButtonsFilterField } from "ui";

import {
  COMMISSION_FROM_FILTER_SEARCH_PARAMETER,
  COMMISSION_STATUS_FILTER_SEARCH_PARAMETER,
  COMMISSION_TO_FILTER_SEARCH_PARAMETER,
  ResellerCommissionStatus
} from "../../../constants";
import {
  ResellerCommissionsFilterFormProps,
  ResellerCommissionsFilterFormValues
} from "./ResellerCommissionsFilterForm.types";

import styles from "./ResellerCommissionsFilterForm.module.scss";

const ALL_STATUSES_VALUE = "All";

const EMPTY_VALUES: ResellerCommissionsFilterFormValues = {
  commissionStatus: ALL_STATUSES_VALUE,
  dateRange: null
};

/**
 * Status and period live in the popover; the source lives in the chips above the
 * table, because switching source is the one filter people flip constantly.
 */
const ResellerCommissionsFilterForm = ({
  closeFilter
}: ResellerCommissionsFilterFormProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const formMethods = useForm<ResellerCommissionsFilterFormValues>({
    defaultValues: EMPTY_VALUES
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const from = searchParams.get(COMMISSION_FROM_FILTER_SEARCH_PARAMETER);
    const to = searchParams.get(COMMISSION_TO_FILTER_SEARCH_PARAMETER);

    reset({
      commissionStatus:
        searchParams.get(COMMISSION_STATUS_FILTER_SEARCH_PARAMETER) ??
        ALL_STATUSES_VALUE,
      dateRange:
        from || to
          ? [from ? new Date(from) : null, to ? new Date(to) : null]
          : null
    });
  }, [searchParams]);

  const clearFilterForm = () => {
    searchParams.delete(COMMISSION_STATUS_FILTER_SEARCH_PARAMETER);
    searchParams.delete(COMMISSION_FROM_FILTER_SEARCH_PARAMETER);
    searchParams.delete(COMMISSION_TO_FILTER_SEARCH_PARAMETER);
    searchParams.delete(PAGE_SEARCH_PARAMETER);

    setSearchParams(searchParams);
    reset(EMPTY_VALUES);
    closeFilter();
  };

  const submitFilterForm = ({
    commissionStatus,
    dateRange
  }: ResellerCommissionsFilterFormValues) => {
    if (commissionStatus && commissionStatus !== ALL_STATUSES_VALUE) {
      searchParams.set(
        COMMISSION_STATUS_FILTER_SEARCH_PARAMETER,
        commissionStatus
      );
    } else {
      searchParams.delete(COMMISSION_STATUS_FILTER_SEARCH_PARAMETER);
    }

    const [from, to] = Array.isArray(dateRange) ? dateRange : [null, null];

    // The backend matches against the order's creation date, so the range has to
    // cover the whole last day or same-day filtering returns nothing.
    if (from) {
      searchParams.set(
        COMMISSION_FROM_FILTER_SEARCH_PARAMETER,
        moment(from).startOf("day").toISOString()
      );
    } else {
      searchParams.delete(COMMISSION_FROM_FILTER_SEARCH_PARAMETER);
    }

    if (to) {
      searchParams.set(
        COMMISSION_TO_FILTER_SEARCH_PARAMETER,
        moment(to).endOf("day").toISOString()
      );
    } else {
      searchParams.delete(COMMISSION_TO_FILTER_SEARCH_PARAMETER);
    }

    searchParams.delete(PAGE_SEARCH_PARAMETER);
    setSearchParams(searchParams);
    closeFilter();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        className={styles.filterForm}
        onSubmit={handleSubmit(submitFilterForm)}
      >
        <div className={styles.filterForm_header}>
          <div className={styles.filterForm_header_title}>{t("Filter by")}</div>
          <div
            className={styles.filterForm_header_clear}
            onClick={clearFilterForm}
          >
            {t("Clear all filters")}
          </div>
        </div>

        <RadioButtonsFilterField
          title={t("Payout status")}
          fieldKey={COMMISSION_STATUS_FILTER_SEARCH_PARAMETER}
          options={[
            { label: t("All"), value: ALL_STATUSES_VALUE, checkedByDefault: true },
            { label: t("Paid"), value: ResellerCommissionStatus.Paid },
            { label: t("Pending"), value: ResellerCommissionStatus.Pending },
            { label: t("Void"), value: ResellerCommissionStatus.Void }
          ]}
        />

        <div className={styles.filterForm_separator} />

        <div>
          <h4 className={styles.filterForm_blockTitle}>
            {t("Order date (from/to)")}
          </h4>

          <Controller
            control={formMethods.control}
            name="dateRange"
            render={({ field: { value, onChange } }) => (
              <DateRangeInput
                value={(value as [Date | null, Date | null]) ?? null}
                onChange={onChange}
              />
            )}
          />
        </div>

        <div className={styles.filterForm_separator} />

        <Button type="submit" className={styles.filterForm_button}>
          {t("Apply")}
        </Button>
      </form>
    </FormProvider>
  );
};

export default ResellerCommissionsFilterForm;
